/**
 * Dock AGORA : Assistant · favoris (5 max) · Pages ouvertes · Modules · Accueil · Messagerie · Téléphone (softphone,
 * agents du pilote qui l'ont activé dans Réglages ; pastille d'état, cerclé quand ça sonne) · Notifications · Profil, panneaux
 * rattachés à leur bouton (un seul à la fois, Échap / second appui / clic extérieur pour fermer ; Radix Popover dans
 * design/dock.ts : focus placé dans le panneau à l'ouverture et rendu au bouton à la fermeture), personnalisation des
 * favoris (appui long, menu Profil, « + » depuis le catalogue des modules). Menu Profil : Trousseau (mots de passe, si
 * l'outil est lisible) ouvre Outils › Trousseau. Scribe (enregistreur de réunions, si l'utilisateur y a droit et l'a activé
 * dans ses Réglages, ou tant qu'une réunion est en cours) : panneau Scribe ; pendant l'enregistrement, bouton rouge,
 * point qui clignote et temps écoulé, nom « Scribe prend des notes · 00:42:15 » ; il ne passe jamais dans le menu
 * Profil pendant l'enregistrement, même sur téléphone (les autres boutons lui laissent la place). Enregistrement dans un
 * autre onglet : même bouton rouge, nom « Scribe prend des notes dans un autre onglet · 00:42:15 ».
 * Pages : clic = afficher la page, « + » = l'afficher à côté de la page active, « × » = fermer (confirmation si
 * brouillon) ; Ctrl+Tab ouvre le panneau. Notifications : cloche + pastille des non lues (rafraîchie toutes les 60 s,
 * onglet visible), panneau des 10 dernières (core/notifications). Messagerie (outil personnel, si l'utilisateur y a
 * droit et l'a activée dans ses Réglages) : enveloppe + pastille des non lus de la boîte de réception (60 s, onglet
 * visible) ; clic = messagerie « mini » (10 derniers messages, lecture rapide, « Voir la boîte mail »), Ctrl/⌘ + clic =
 * écran Outils › Messagerie dans une nouvelle page. Toute action vers un écran suit la règle de l'espace de travail : clic =
 * dans la page active, Ctrl/⌘ + clic = nouvelle page.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FAVORIS_MAX, RESSOURCE_MESSAGERIE, RESSOURCE_SCRIBE, RESSOURCE_TROUSSEAU, ROUTE_MESSAGERIE, ROUTE_TROUSSEAU, type MetierId, type NoeudCatalogue,
  type NonLusMessagerie,
} from '@agora/shared';
import { AiAssistant, Dock, Menu, OpenPages, ShortcutPicker, type CibleDock, type DockFavori, type ShortcutPickerProps, type Status } from '../../design';
import { useT } from '../i18n';
import { useSocle } from '../contexte';
import { useFormats } from '../../socle';
import { initiales, useLargeur } from './utilitaires';
import { ROUTE_ADMIN_MODULES } from './BarreLaterale';
import { useActionsEspace, useEtatEspace, useQuitterAgora } from '../pages/espace';
import { LARGEUR_COTE_A_COTE } from '../pages/Volets';
import { ROUTE_NOTIFICATIONS, useNotificationsDock } from '../notifications';
import { PanneauNotifications } from '../notifications/PanneauNotifications';
import { EVENEMENT_TELEPHONE, PanneauSoftphone, STATUT_AGENT, useSoftphone } from '../telephonie/softphone/Softphone';
import { EVENEMENT_SCRIBE, useScribe } from '../scribe/Scribe';
import { PanneauScribe, useChrono } from '../scribe/PanneauScribe';
import type { EtatScribe, PhaseScribe } from '../scribe/enregistreur';

/** Messagerie « mini » : chargée à la première ouverture du panneau (avec les styles de la messagerie). */
const MiniMessagerie = React.lazy(() => import('../../metiers/outils/messagerie/MiniMessagerie'));

interface Props {
  idNoeudActif?: string;
  accueilActif: boolean;
  titrePage: string;
  compact: boolean;
}

export function DockAgora({ idNoeudActif, accueilActif, titrePage, compact }: Props) {
  const { t, lib } = useT();
  const f = useFormats();
  const { moi, preferences, modifierPreferences, catalogue, noeuds, deconnecter, api } = useSocle();
  const { pages, volets, actif, titreDe, infosDe } = useEtatEspace();
  const espace = useActionsEspace();
  const quitterAgora = useQuitterAgora();
  const largeurEcran = useLargeur();
  const coteAcote = largeurEcran >= LARGEUR_COTE_A_COTE;
  const [panneau, setPanneau] = React.useState<CibleDock | null>(null);
  const [edition, setEdition] = React.useState(false);
  const [messages, setMessages] = React.useState<{ from: 'user' | 'ai'; text: string }[]>([]);

  // Ctrl+Tab (charte) : panneau des pages ouvertes (le dock place le focus sur la première carte).
  React.useEffect(() => {
    const ouvrirPanneau = () => setPanneau('pages');
    window.addEventListener('agora:pages', ouvrirPanneau);
    return () => window.removeEventListener('agora:pages', ouvrirPanneau);
  }, []);

  // Softphone : appel qui sonne ou appel en un clic → panneau Téléphone ouvert.
  const softphone = useSoftphone();
  React.useEffect(() => {
    const ouvrirTelephone = () => { setEdition(false); setPanneau('tel'); };
    window.addEventListener(EVENEMENT_TELEPHONE, ouvrirTelephone);
    return () => window.removeEventListener(EVENEMENT_TELEPHONE, ouvrirTelephone);
  }, []);
  // Scribe : écran « Démarrer une réunion » → panneau Scribe ouvert (même sans bouton : panneau centré sur le dock).
  const scribe = useScribe();
  React.useEffect(() => {
    const ouvrirScribe = () => { setEdition(false); setPanneau('scribe'); };
    window.addEventListener(EVENEMENT_SCRIBE, ouvrirScribe);
    return () => window.removeEventListener(EVENEMENT_SCRIBE, ouvrirScribe);
  }, []);
  const phaseScribe: PhaseScribe = scribe?.etat.phase ?? 'inactif';
  // Enregistrement d'un autre onglet (état diffusé par celui-ci) : même bouton rouge, avec sa durée et sa pause.
  const ailleursScribe = !!scribe && phaseScribe !== 'enregistrement' && phaseScribe !== 'envoi' && scribe.autreOnglet?.phase === 'enregistrement'
    ? scribe.autreOnglet : null;
  const etatScribe = ailleursScribe ?? scribe?.etat;
  const chronoScribe = useChrono(etatScribe);
  const enregistreScribe = phaseScribe === 'enregistrement' || !!ailleursScribe;
  // Bouton : option des Réglages, réunion en cours (enregistrement, envoi, à reprendre, traitement) dans cet onglet, ou
  // enregistrement dans un autre onglet.
  const boutonScribe = !!scribe && (preferences.scribe || (phaseScribe !== 'inactif' && !scribe.ailleurs) || !!ailleursScribe);
  const libelleScribe = lib(RESSOURCE_SCRIBE);
  const nomScribe = nomBoutonScribe(scribe, ailleursScribe, chronoScribe, libelleScribe, t);
  const statutScribe: Status | undefined = !scribe || enregistreScribe ? undefined
    : phaseScribe === 'envoi' ? (scribe.etat.horsLigne ? 'waiting' : 'progress')
      : phaseScribe === 'a-reprendre' && !scribe.ailleurs ? 'waiting' : undefined;
  const libelleTelephone = softphone ? `${t('service.telephonie.softphone.titre')} · ${t(`service.telephonie.softphone.etats.${softphone.etat.agent}`)}` : undefined;

  const basculer = (c: CibleDock) => setPanneau((p) => (p === c ? null : c));
  const aller = (route: string) => { setPanneau(null); espace.ouvrir(route); };

  // Notifications : 10 dernières + nombre de non lues (pastille), rechargées à l'ouverture du panneau ; le dock place
  // le focus sur la première notification et le rend à la cloche à la fermeture.
  const notifs = useNotificationsDock();
  const nonLues = notifs.data?.nonLues ?? 0;
  const { refetch: rechargerNotifs } = notifs;
  const basculerNotifs = () => {
    if (panneau !== 'notifs') void rechargerNotifs();
    basculer('notifs');
  };
  const fermerNotifs = () => setPanneau(null);

  // Messagerie : droit « lire » sur l'outil (catalogue) ET option des Réglages ; compteur des non lus de la réception
  // (clé ['messagerie', 'non-lus'], rafraîchie aussi par l'écran de la messagerie après lecture ou classement).
  const messagerie = !!noeuds.get(RESSOURCE_MESSAGERIE)?.droits.lire && preferences.messagerie;
  // Trousseau (mots de passe) : entrée du menu Profil, si l'outil est lisible (catalogue : droit et activation).
  const trousseau = !!noeuds.get(RESSOURCE_TROUSSEAU)?.droits.lire;
  const compteurBoite = useQuery({
    queryKey: ['messagerie', 'non-lus'],
    queryFn: ({ signal }) => api.get<NonLusMessagerie>('/v1/outils/messagerie/non-lus', undefined, signal),
    enabled: messagerie,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const nonLusBoite = messagerie ? compteurBoite.data?.nonLus ?? 0 : 0;
  const libelleBoite = t('outils.messagerie.dock.libelle');
  const libelleNonLusBoite = nonLusBoite ? `${libelleBoite} · ${t('outils.messagerie.dock.nonLus', { n: f.nombre(nonLusBoite) })}` : undefined;
  const ouvrirBoite = (e?: React.MouseEvent, message?: string | null) => {
    setPanneau(null);
    espace.ouvrir(message ? `${ROUTE_MESSAGERIE}?message=${encodeURIComponent(message)}` : ROUTE_MESSAGERIE, { evenement: e });
  };
  const { refetch: rechargerBoite } = compteurBoite;
  const basculerBoite = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) { ouvrirBoite(e); return; }
    if (panneau !== 'mail') void rechargerBoite();
    basculer('mail');
  };

  const favoris: DockFavori[] = preferences.favoris
    .map((id) => noeuds.get(id))
    .filter((n): n is NoeudCatalogue => !!n)
    .map((n) => ({ id: n.id, label: lib(n.id), icon: n.icone, metier: n.metier as MetierId | undefined }));

  // Dock compact (mobile) : ≈ 42 px par bouton (+ marges et séparateurs). Si la cloche le ferait déborder de l'écran
  // (ex. 3 favoris à 375 px), elle est retirée : la pastille passe sur l'avatar et les notifications restent
  // accessibles par le menu Profil (entrée « Notifications »).
  // Puis l'enveloppe de la messagerie, retirée avant la cloche : elle reste dans le menu Profil (entrée « Messagerie »).
  // Le téléphone n'existe pas sur un écran étroit (softphone inactif), il compte quand même s'il est là.
  // Scribe : pendant l'enregistrement, toujours dans le dock (la cloche et l'enveloppe lui cèdent la place) ; sinon
  // retiré en premier, il passe dans le menu Profil (entrée « Scribe »).
  const telephone = softphone ? 1 : 0;
  const place = (boutons: number) => !compact || 24 + 42 * (Math.min(favoris.length, FAVORIS_MAX) + boutons + telephone) <= largeurEcran - 8;
  const fixes = 5 + (boutonScribe && enregistreScribe ? 1 : 0);
  const cloche = place(fixes + 1);
  const enveloppe = messagerie && cloche && place(fixes + 2);
  const scribeDock = boutonScribe && (enregistreScribe || place(fixes + 1 + (cloche ? 1 : 0) + (enveloppe ? 1 : 0)));
  const libelleNonLues = nonLues ? t('notifications.nonLues', { n: f.nombre(nonLues) }) : undefined;
  const ini = initiales(moi.prenom, moi.nom, moi.email);
  React.useEffect(() => { if (!cloche) setPanneau((p) => (p === 'notifs' ? null : p)); }, [cloche]);
  React.useEffect(() => { if (!enveloppe) setPanneau((p) => (p === 'mail' ? null : p)); }, [enveloppe]);

  const basculerFavori = (id: string) => modifierPreferences((p) => ({
    ...p, favoris: p.favoris.includes(id) ? p.favoris.filter((x) => x !== id) : p.favoris.length < FAVORIS_MAX ? [...p.favoris, id] : p.favoris,
  }));

  // Punaise du panneau Modules : épingle / retire ; dock plein → « Retirez d'abord un favori » (règle du design).
  const [avisFavoris, setAvisFavoris] = React.useState<string | undefined>();
  React.useEffect(() => {
    if (!avisFavoris) return;
    const id = window.setTimeout(() => setAvisFavoris(undefined), 4000);
    return () => window.clearTimeout(id);
  }, [avisFavoris]);
  React.useEffect(() => { if (panneau !== 'mods') setAvisFavoris(undefined); }, [panneau]);
  const epingler = (id: string) => {
    if (!preferences.favoris.includes(id) && preferences.favoris.length >= FAVORIS_MAX) { setAvisFavoris(t('dock.favorisPlein', { max: FAVORIS_MAX })); return; }
    setAvisFavoris(undefined);
    basculerFavori(id);
  };

  // Catalogue « Modules » : modules et sous-modules lisibles, groupés par métier.
  const groupes: ShortcutPickerProps['groups'] = (catalogue?.metiers || [])
    .filter((m) => m.enfants.length)
    .map((m) => ({
      metier: m.id as MetierId, label: lib(m.id),
      items: m.enfants.flatMap((mo) => [mo, ...mo.enfants]).map((n) => ({ id: n.id, label: lib(n.id), icon: n.icone, pinned: preferences.favoris.includes(n.id) })),
    }));

  // Pages ouvertes : visibles d'abord (active, puis à côté), puis les autres de la plus récente à la plus ancienne.
  const idActive = volets[actif];
  const visibles = volets.length === 2 && coteAcote ? volets : [idActive];
  const ordre = (id: string) => (id === idActive ? 0 : visibles.includes(id) ? 1 : 2);
  const listePages = [...pages].sort((a, b) => ordre(a.id) - ordre(b.id) || b.vueLe - a.vueLe);

  const nomComplet = [moi.prenom, moi.nom].filter(Boolean).join(' ') || moi.email;
  let contenu: React.ReactNode = null;
  let largeur: number | string | undefined;
  if (panneau === 'ai') {
    largeur = 'min(420px, calc(100vw - 24px))';
    contenu = (
      <AiAssistant
        anchored title={t('assistant.titre')} contextLabel={t('assistant.contexte', { page: titrePage })}
        messages={[{ from: 'ai', text: t('assistant.bientot') }, ...messages]}
        placeholder={t('assistant.placeholder')} sendLabel={t('assistant.envoyer')} closeLabel={t('commun.fermer')} disclaimer={t('assistant.rgpd')}
        onClose={() => setPanneau(null)}
        onSend={(texte) => setMessages((m) => [...m, { from: 'user', text: texte }, { from: 'ai', text: t('assistant.bientot') }])}
      />
    );
  } else if (panneau === 'pages') {
    largeur = 'min(760px, calc(100vw - 24px))';
    contenu = (
      <OpenPages
        title={t('dock.pagesTitre')} hint={t('dock.pagesAide')} closeLabel={t('dock.fermerPage')} activeId={idActive}
        visibleIds={visibles} splitLabel={coteAcote ? t('dock.ouvrirACote') : undefined}
        pages={listePages.map((p) => {
          const { metier, module } = infosDe(p);
          const etat = [p.etat, p.brouillon ? t('dock.brouillon') : undefined].filter(Boolean).join(' · ') || undefined;
          const quand = p.id === idActive ? t('dock.pageActive') : visibles.includes(p.id) ? t('dock.pageACote') : f.relatif(new Date(p.vueLe));
          return { id: p.id, metier, moduleLabel: module, title: titreDe(p), state: etat, time: quand };
        })}
        onOpen={(id) => { setPanneau(null); espace.afficher(id); }}
        onSplit={coteAcote ? (id) => { setPanneau(null); espace.aCote(id); } : undefined}
        onClose={(id) => espace.fermer(id)}
      />
    );
  } else if (panneau === 'mods') {
    largeur = 'min(560px, calc(100vw - 24px))';
    contenu = (
      <ShortcutPicker
        title={edition ? t('dock.favorisTitre') : t('dock.modules')}
        countLabel={t('dock.favorisCompte', { n: preferences.favoris.length, max: FAVORIS_MAX })}
        groups={groupes}
        onToggle={(id) => {
          if (edition) basculerFavori(id);
          else { const n = noeuds.get(id); if (n) aller(n.route); }
        }}
        // Hors personnalisation : la tuile ouvre le module, la punaise l'épingle au dock.
        onPin={edition ? undefined : epingler}
        pinLabel={t('dock.epingler')} unpinLabel={t('dock.desepingler')}
        notice={avisFavoris}
      />
    );
  } else if (panneau === 'mail') {
    largeur = 'min(440px, calc(100vw - 24px))';
    contenu = (
      <React.Suspense fallback={<section className="agx-notifs ag-glass ag-glass-strong"><p className="agx-notifs-vide" role="status">{t('app.chargement')}</p></section>}>
        <MiniMessagerie nonLus={nonLusBoite} voirBoite={(message, e) => ouvrirBoite(e, message)} fermer={() => setPanneau(null)} />
      </React.Suspense>
    );
  } else if (panneau === 'notifs') {
    largeur = 'min(420px, calc(100vw - 24px))';
    contenu = <PanneauNotifications page={notifs.data} chargement={notifs.isLoading} erreur={notifs.error} fermer={fermerNotifs} />;
  } else if (panneau === 'tel' && softphone) {
    largeur = 'min(360px, calc(100vw - 24px))';
    contenu = <PanneauSoftphone fermer={() => setPanneau(null)} />;
  } else if (panneau === 'scribe' && scribe) {
    largeur = 'min(380px, calc(100vw - 24px))';
    contenu = <PanneauScribe fermer={() => setPanneau(null)} />;
  } else if (panneau === 'me') {
    contenu = (
      <Menu
        title={nomComplet} ariaLabel={t('profil.monProfil')}
        items={[
          { id: 'profil', label: t('profil.monProfil'), icon: 'user' },
          { id: 'notifications', label: libelleNonLues ? `${t('notifications.titre')} · ${libelleNonLues}` : t('notifications.titre'), icon: 'bell' },
          ...(messagerie && !enveloppe ? [{ id: 'messagerie', label: libelleNonLusBoite ?? libelleBoite, icon: 'mail' as const }] : []),
          ...(boutonScribe && !scribeDock ? [{ id: 'scribe', label: nomScribe ?? libelleScribe, icon: noeuds.get(RESSOURCE_SCRIBE)?.icone ?? 'edit' }] : []),
          ...(trousseau ? [{ id: 'trousseau', label: t('profil.trousseau'), icon: 'key' as const }] : []),
          { id: 'reglages', label: t('profil.reglages'), icon: 'settings' },
          { id: 'dock', label: t('dock.personnaliser'), icon: 'edit' },
          { id: 'aide', label: t('profil.aide'), icon: 'book' },
          // Administrateurs : accès à l'administration aussi sur mobile (barre latérale masquée)
          ...(moi.admin ? [{ id: 'admin', label: t('admin.modules.titre'), icon: 'grid' as const }] : []),
          { divider: true },
          { id: 'sortie', label: t('profil.deconnexion'), icon: 'arrow' },
        ]}
        onSelect={(id) => {
          if (id === 'profil') aller('/reglages#compte');
          else if (id === 'notifications') aller(ROUTE_NOTIFICATIONS);
          else if (id === 'messagerie') ouvrirBoite();
          else if (id === 'trousseau') aller(ROUTE_TROUSSEAU);
          else if (id === 'scribe') setPanneau('scribe');
          else if (id === 'reglages') aller('/reglages');
          else if (id === 'admin') aller(ROUTE_ADMIN_MODULES);
          else if (id === 'aide') aller('/reglages#aide');
          else if (id === 'dock') { setPanneau(null); setEdition(true); }
          else if (id === 'sortie') { setPanneau(null); void quitterAgora(deconnecter); }
        }}
      />
    );
  }

  return (
    <div className="ag-dock-wrap">
      <div className="ag-dock-anchor">
        <Dock
          onPopoverClose={() => setPanneau(null)}
          compact={compact}
          popover={contenu && panneau ? { target: edition && panneau === 'mods' ? '__add' : panneau, content: contenu, width: largeur } : null}
          ariaLabel={t('dock.menu')}
          aiLabel={t('dock.assistant')} onAi={() => basculer('ai')} aiOpen={panneau === 'ai'}
          favorites={favoris} activeId={idNoeudActif} max={FAVORIS_MAX}
          onSelect={(id) => { const n = noeuds.get(id); if (n) aller(n.route); }}
          pagesLabel={t('dock.pages')} pagesCount={pages.length} pagesOpen={panneau === 'pages'} onPages={() => basculer('pages')}
          pagesAriaLabel={pages.length ? `${t('dock.pages')} · ${t('dock.pagesOuvertes', { n: f.nombre(pages.length) })}` : undefined}
          modulesLabel={t('dock.modules')} onModules={() => basculer('mods')}
          homeLabel={t('dock.accueil')} onHome={() => aller('/')} homeActive={accueilActif}
          mailLabel={enveloppe ? libelleBoite : undefined} mailCount={nonLusBoite} mailAriaLabel={libelleNonLusBoite}
          mailActive={idNoeudActif === RESSOURCE_MESSAGERIE} mailOpen={panneau === 'mail'} onMail={basculerBoite}
          phoneLabel={softphone ? t('service.telephonie.softphone.titre') : undefined} phoneAriaLabel={libelleTelephone}
          phoneOpen={panneau === 'tel'} onPhone={() => basculer('tel')}
          phoneStatus={softphone ? STATUT_AGENT[softphone.etat.agent] : undefined} phoneRinging={softphone?.etat.appel?.etat === 'sonnerie'}
          scribeLabel={scribeDock ? libelleScribe : undefined} scribeAriaLabel={nomScribe} scribeOpen={panneau === 'scribe'}
          scribeRecording={enregistreScribe} scribePaused={enregistreScribe && !!etatScribe?.pause} scribeTime={enregistreScribe ? chronoScribe : undefined} scribeStatus={statutScribe}
          scribeIcon={noeuds.get(RESSOURCE_SCRIBE)?.icone} onScribe={() => basculer('scribe')}
          notificationsLabel={cloche ? t('notifications.dock') : undefined} notificationsCount={nonLues} notificationsOpen={panneau === 'notifs'} onNotifications={basculerNotifs}
          notificationsAriaLabel={libelleNonLues ? `${t('notifications.dock')} · ${libelleNonLues}` : undefined}
          initials={ini} userLabel={nomComplet} userOpen={panneau === 'me'} onUser={() => basculer('me')}
          userCount={cloche ? undefined : nonLues} userAriaLabel={!cloche && libelleNonLues ? `${ini} · ${nomComplet} · ${libelleNonLues}` : undefined}
          editing={edition} addLabel={t('dock.ajouter')} doneLabel={t('dock.terminer')} removeLabel={t('dock.retirer')}
          onAdd={() => setPanneau('mods')}
          onRemove={(id) => modifierPreferences((p) => ({ ...p, favoris: p.favoris.filter((x) => x !== id) }))}
          onDone={() => { setEdition(false); setPanneau(null); }}
          onLongPress={() => setEdition(true)}
        />
      </div>
    </div>
  );
}

/**
 * Nom du bouton Scribe (et entrée du menu Profil) selon l'état : « Scribe prend des notes · 00:42:15 » (« Scribe en pause
 * · 00:42:15 » après 60 s de silence ; + « Hors ligne · Parties en attente : n »), « Scribe prend des notes dans un autre
 * onglet · 00:42:15 » (« Scribe en pause dans un autre onglet · … », `ailleurs` : état diffusé par l'onglet qui
 * enregistre), « Scribe · Envoi de l'enregistrement », « Scribe · Réunion à reprendre », « Scribe · Dans un autre
 * onglet » ; undefined au repos (le libellé suffit).
 */
function nomBoutonScribe(
  scribe: ReturnType<typeof useScribe>, ailleurs: EtatScribe | null, chrono: string, libelle: string, t: ReturnType<typeof useT>['t'],
): string | undefined {
  if (!scribe) return undefined;
  if (ailleurs) {
    const nom = t(ailleurs.pause ? 'outils.scribe.dock.enPauseAilleurs' : 'outils.scribe.dock.enregistreAilleurs', { duree: chrono });
    return [nom, ailleurs.horsLigne ? t('outils.scribe.dock.horsLigne', { n: ailleurs.enAttente }) : undefined].filter(Boolean).join(' · ');
  }
  const { phase, horsLigne, enAttente } = scribe.etat;
  const horsLigneTexte = horsLigne ? t('outils.scribe.dock.horsLigne', { n: enAttente }) : undefined;
  if (phase === 'enregistrement') {
    const nom = scribe.etat.pause ? t('outils.scribe.dock.enPause', { duree: chrono }) : t('outils.scribe.dock.enregistre', { duree: chrono });
    return [nom, horsLigneTexte].filter(Boolean).join(' · ');
  }
  if (phase === 'envoi') return `${libelle} · ${horsLigneTexte ?? t('outils.scribe.dock.envoi')}`;
  if (scribe.ailleurs) return `${libelle} · ${t('outils.scribe.dock.ailleurs')}`;
  if (phase === 'a-reprendre') return `${libelle} · ${t('outils.scribe.dock.aReprendre')}`;
  return undefined;
}
